import React, { useMemo, useState, useEffect, useRef } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtTabController from "../../components/NtTabController";
import NtModalInputField from "../../components/NtModalInputField";
import { useTheme } from "../../context/ThemeContext";
import { isJsonObject } from "../../common/utils/ControllerUtils";

const types = [
  { id: "text", title: "Free text" },
  { id: "value", title: "Structured" },
];

// A JSON object with a `title` or `organization` key means the description was
// authored as structured data — land the user on that tab instead of an empty
// Free text field.
const detectInitialType = (value) => {
  if (isJsonObject(value)) {
    const parsed = JSON.parse(value);
    if (parsed && (parsed.title || parsed.organization)) return types[1];
  }
  return types[0];
};

const parseStruct = (value) => {
  if (!isJsonObject(value)) return { title: "", organization: "" };
  const parsed = JSON.parse(value);
  return {
    title: parsed?.title || "",
    organization: parsed?.organization || "",
  };
};

function NtExtensionDescriptionInputField({
  containerStyle,
  title = "Description",
  value,
  setValue,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [type, setType] = useState(() => detectInitialType(value));
  const [textValue, setTextValue] = useState(() =>
    isJsonObject(value) ? "" : value || ""
  );
  const [structValue, setStructValue] = useState(() => parseStruct(value));

  // The parent often hydrates `value` asynchronously (extension fetch lands
  // after this component mounts). Re-detect once when a real value arrives so
  // a JSON description lands on the Structured tab instead of Free text.
  const initializedRef = useRef(!!value);
  useEffect(() => {
    if (initializedRef.current) return;
    if (value) {
      setType(detectInitialType(value));
      setTextValue(isJsonObject(value) ? "" : value);
      setStructValue(parseStruct(value));
      initializedRef.current = true;
    }
  }, [value]);

  const handleTabChange = (newType) => {
    setType(newType);
    if (newType.id === "text") {
      setValue(textValue || "");
    } else {
      const hasAny = structValue.title || structValue.organization;
      setValue(hasAny ? JSON.stringify(structValue) : "");
    }
  };

  const handleTextChange = (text) => {
    setTextValue(text);
    setValue(text);
  };

  const handleStructChange = (name, input) => {
    const next = { ...structValue, [name]: input };
    setStructValue(next);
    const hasAny = next.title || next.organization;
    setValue(hasAny ? JSON.stringify(next) : "");
  };

  return (
    <View style={containerStyle}>
      <View style={styles.head}>
        <NtModalInputTitle>{title}</NtModalInputTitle>
        <NtTabController
          options={types}
          selection={type}
          onPress={handleTabChange}
        />
      </View>

      {type?.id === "text" && (
        <NtModalInputField
          placeholder="e.g. Front desk · Lobby phone"
          value={textValue}
          setValue={handleTextChange}
        />
      )}

      {type?.id === "value" && (
        <View style={{ flexDirection: "row" }}>
          <NtModalInputField
            containerStyle={{ flex: 1 }}
            title="Title"
            placeholder="e.g. Front desk"
            value={structValue.title}
            setValue={(text) => handleStructChange("title", text)}
          />
          <NtModalInputField
            containerStyle={{ flex: 1, marginLeft: 10 }}
            title="Organization"
            placeholder="e.g. Operations"
            value={structValue.organization}
            setValue={(text) => handleStructChange("organization", text)}
          />
        </View>
      )}

      <NtText style={styles.footer}>
        {type?.id === "text"
          ? "Switch to Structured to set Title and Organization separately."
          : "Switch to Free text if you'd rather just type a single description."}
      </NtText>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    head: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginLeft: 4,
      marginBottom: 6,
    },
    footer: {
      fontSize: 11,
      color: t.textMute,
      fontStyle: "italic",
      marginTop: 4,
      marginLeft: 4,
    },
  });

export default NtExtensionDescriptionInputField;
